import { __assign, __awaiter, __decorate, __extends, __generator, __metadata, __param } from "tslib";
import { Aggregate } from '../../base/ddd-types/aggregate';
import { dataFieldNamingHandler, exceptionHandler } from '../../base/decorators';
import { safeParseObject } from '../../base/utils';
import { PersonalizeAnonymousStatus, } from './collaborate-aggregate.interface';
import { getUidFormUserId, isLoggingUser } from './collaborate-utils';
import { CollaborateRepository } from '../repository/collaborate-repository';
import { Collaborate } from '../value-object/collaborate';
import { IDocMetaDomainService } from '../../doc-meta/service/doc-meta-domain-service.interface';
import { IUserInfoDomainService } from '../../user-info/service/user-info-domain-service.interface';
import { Emitter } from '@tencent/ark-utils/lib/event';
import { isArray } from '@tencent/ark-utils/lib/types';
import { ServerMessageNoticeEventType } from '@tencent/docs-connection';
/**
 * 协作者信息列表最高数量上限
 * 用于优化大量数据场景下的界面卡顿问题
 */
var MAX_COLLAB_LIST_LENGTH = 999;
/**
 * 节流批处理 interval
 * 先预设 1分钟 请求一次
 * */
var THROTTLE_INTERVAL = 60 * 1000;
var CollaborateAggregate = /** @class */ (function (_super) {
    __extends(CollaborateAggregate, _super);
    function CollaborateAggregate(id, docMetaDomain, userInfoDomain) {
        var _this = _super.call(this, id) || this;
        _this.docMetaDomain = docMetaDomain;
        _this.userInfoDomain = userInfoDomain;
        _this.collaborateList = [];
        _this.currentMemCount = 0;
        _this.collabAllInfoList = [];
        _this.throttleInterval = 0;
        /**
         * 协作者列表长度最高上限字段
         * 设置为 0 或 undefined 等同于 不限制长度
         */
        _this.maxCollabListLengthLimit = MAX_COLLAB_LIST_LENGTH;
        /**
         * 进出房间层记录流水记录
         */
        _this.collabRecordList = [];
        /**
         * 为了避免反复进出文档，这里用缓存缓存一下已经进过房间的协作者信息
         */
        _this.collaboratesCacheMap = new Map();
        /**
         * Emitters
         */
        _this.onCollabAllInfoListUpdatedEmitter = new Emitter();
        _this.onUserComeInEmitter = new Emitter();
        _this.onUserLeaveEmitter = new Emitter();
        _this.onCommercializeMessageEmitter = new Emitter();
        /**
         * 需要请求资料的 userId 集合，请求成功或者 leave 的 userId 才会移除出队列，会兜底拉取资料失败的情况
         */
        _this.needRequestUserIds = new Set();
        _this.onCollaboratorCountChangedEmitter = new Emitter();
        _this.onCollabAllInfoListUpdated = _this.onCollabAllInfoListUpdatedEmitter.event;
        _this.onUserComeIn = _this.onUserComeInEmitter.event;
        _this.onUserLeave = _this.onUserLeaveEmitter.event;
        _this.onCommercializeMessage = _this.onCommercializeMessageEmitter.event;
        _this.onCollaboratorCountChanged = _this.onCollaboratorCountChangedEmitter.event;
        _this.collaborateRepository = new CollaborateRepository(docMetaDomain);
        return _this;
    }
    CollaborateAggregate_1 = CollaborateAggregate;
    CollaborateAggregate.prototype.handleUserComeIn = function (notices) {
        var _this = this;
        var _a;
        // @deprecate 2.6.11, 旧方案兼容: 透传房间层协作者用户信息
        // TODO:  待等业务方全更新后再删除
        (_a = notices.notices) === null || _a === void 0 ? void 0 : _a.forEach(function (notice) {
            _this.appendNoticeTo(notice);
        });
        this.currentMemCount = this.collaborateList.length;
        this.onUserComeInEmitter.fire(notices);
        // 处理房间层事件: 用户进入房间
        this.processUserEnter(notices);
    };
    CollaborateAggregate.prototype.handleUserLeave = function (notices) {
        var _this = this;
        var _a;
        // @deprecate 2.6.11, 旧方案兼容: 透传房间层协作者用户信息
        // TODO:  待等业务方全更新后再删除
        (_a = notices.notices) === null || _a === void 0 ? void 0 : _a.forEach(function (notice) {
            _this.collaborateList = _this.collaborateList.filter(function (item) { return item.userId !== notice.userId; });
        });
        this.currentMemCount = this.collaborateList.length;
        this.onUserLeaveEmitter.fire(notices);
        // 处理房间层事件: 用户离开房间
        this.processUserLeave(notices);
    };
    CollaborateAggregate.prototype.getUserMarkName = function (userIds_1) {
        return __awaiter(this, arguments, void 0, function (userIds, disableCache, params) {
            var rspData_1, _a, unCachedUserIds, cachedResults, rspData, _b, rsp;
            var _c, _d;
            if (disableCache === void 0) { disableCache = false; }
            return __generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        if (!disableCache) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.collaborateRepository.getUserMarkName(userIds, params)];
                    case 1:
                        rspData_1 = _e.sent();
                        this.setUserMarkItemToCache(rspData_1);
                        return [2 /*return*/, rspData_1];
                    case 2:
                        _a = this.getUserMarkItemFromCache(userIds), unCachedUserIds = _a.unCachedUserIds, cachedResults = _a.cachedResults;
                        // 当所有 userId 均命中缓存，则直接返回结果
                        if (unCachedUserIds.length === 0) {
                            return [2 /*return*/, {
                                    data: { cgicode: 0, retcode: 0, result: { rsp: cachedResults } },
                                    status: 200,
                                    statusText: '',
                                }];
                        }
                        // 当前用户未登录时，直接返回，不发送请求，因为请求也要鉴权
                        if (!isLoggingUser(this.userInfoDomain.userInfoAggregate.userInfo.userId)) {
                            return [2 /*return*/, {
                                    data: { cgicode: 0, retcode: 0, result: { rsp: [] } },
                                    status: 200,
                                    statusText: '',
                                }];
                        }
                        return [4 /*yield*/, this.collaborateRepository.getUserMarkName(unCachedUserIds, params)];
                    case 3:
                        rspData = _e.sent();
                        // 缓存请求回来的数据
                        this.setUserMarkItemToCache(rspData);
                        // 将获取到的结果与缓存结果合并后返回
                        // 注意：此处未对结果进行排序， 如有排序需求，sort rsp 即可
                        if ((_d = (_c = rspData === null || rspData === void 0 ? void 0 : rspData.data) === null || _c === void 0 ? void 0 : _c.result) === null || _d === void 0 ? void 0 : _d.rsp) {
                            _b = rspData.data.result.rsp, rsp = _b === void 0 ? [] : _b;
                            rspData.data.result.rsp = rsp.concat(cachedResults);
                        }
                        return [2 /*return*/, rspData];
                }
            });
        });
    };
    /**
     * 设置匿名
     * @param status 从未设置：0 匿名状态 1: 开启 2: 关闭
     * @param file_id
     */
    CollaborateAggregate.prototype.setPersonalizeAnonymous = function (status, file_id) {
        return __awaiter(this, void 0, void 0, function () {
            var rspData;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.collaborateRepository.setPersonalizeAnonymous(status, file_id)];
                    case 1:
                        rspData = _a.sent();
                        return [2 /*return*/, rspData];
                }
            });
        });
    };
    /**
     * 查询匿名设置
     * @param file_id
     */
    CollaborateAggregate.prototype.getPersonalizeAnonymous = function (file_id) {
        return __awaiter(this, void 0, void 0, function () {
            var rspData;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.collaborateRepository.getPersonalizeAnonymous(file_id)];
                    case 1:
                        rspData = _a.sent();
                        return [2 /*return*/, rspData];
                }
            });
        });
    };
    /**
     * 处理协作者会员信息变更通知
     * @param data 商业化推送数据
     */
    CollaborateAggregate.prototype.handleCommerIcilizeMessage = function (data) {
        this.onCommercializeMessageEmitter.fire(data);
    };
    /**
     * 收到服务端Socket事件
     */
    CollaborateAggregate.prototype.onReceiveMessageFromServer = function (notice) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, payload, type;
            return __generator(this, function (_b) {
                _a = notice || {}, payload = _a.payload, type = _a.type;
                if (type === ServerMessageNoticeEventType.COLLAB_COUNT_CHANGE && (payload === null || payload === void 0 ? void 0 : payload.collab_count) !== undefined) {
                    this.onCollaboratorCountChangedEmitter.fire({ count: payload.collab_count });
                }
                return [2 /*return*/];
            });
        });
    };
    Object.defineProperty(CollaborateAggregate.prototype, "maxCollabListLimit", {
        /**
         * 获取协作者列表最大长度上限
         */
        get: function () {
            if (this.maxCollabListLengthLimit) {
                return this.maxCollabListLengthLimit;
            }
            return Infinity;
        },
        enumerable: false,
        configurable: true
    });
    CollaborateAggregate.prototype.appendNoticeTo = function (notice) {
        var findNotice = this.collaborateList.find(function (item) { return item.userId === notice.userId; });
        if (findNotice) {
            return;
        }
        this.collaborateList.push(notice);
    };
    CollaborateAggregate.prototype.createCollaborateItemObject = function (collaborate, isGuest) {
        var _a;
        var collaborateObj = __assign(__assign(__assign({}, collaborate), (!collaborate.userId ? { userId: (_a = collaborate.reqUid) !== null && _a !== void 0 ? _a : '' } : null)), { userType: isGuest ? 1 /* USER_TYPE.Guest */ : 2 /* USER_TYPE.Person */ });
        return collaborateObj;
    };
    /**
     * 后台设计的有坑
     * push：             有 vipInfo， 无用户头像等信息
     * getUserMarkName:   无 vipInfo,  但是又有头像等信息
     *
     * 所以需要在生成最终数据时，要从 recordList 缓存里取一下通过带过来的 vipInfo 给添上去；
     *
     */
    CollaborateAggregate.prototype.fillVipInfoWithRecordInfo = function (collaborateObj, isGuest) {
        if (isGuest) {
            return collaborateObj;
        }
        var collaborate = this.collabRecordList.find(function (item) { return item.userId === collaborateObj.userId; });
        if (!collaborate) {
            return collaborateObj;
        }
        return __assign(__assign({}, collaborateObj), { vipInfo: collaborate === null || collaborate === void 0 ? void 0 : collaborate.vipInfo });
    };
    // 检查不存在才加入列表
    CollaborateAggregate.prototype.checkAdd2CollaborateList = function (collaborate, isGuest) {
        var collaborateObj = this.createCollaborateItemObject(collaborate, isGuest);
        collaborateObj = this.fillVipInfoWithRecordInfo(collaborateObj, isGuest);
        if (!this.collabAllInfoList.find(function (item) { return item.userId === collaborateObj.userId; })
            && this.collabAllInfoList.length < this.maxCollabListLimit) {
            this.collabAllInfoList.push(new Collaborate(collaborateObj));
        }
    };
    CollaborateAggregate.prototype.processUserEnter = function (notices) {
        var _this = this;
        var hasUpdated = false;
        notices === null || notices === void 0 ? void 0 : notices.notices.forEach(function (notice) {
            // 当前 userId 成功拉取过的话，就不再去拉取了
            var userId = getUidFormUserId(notice.userId);
            var collaborate = {
                uid: userId,
                userId: userId,
                sessionType: notice.sessionType,
                uuid: notice.uuId,
                vipInfo: safeParseObject(notice.vipInfo, {}),
            };
            _this.collabRecordList.push(collaborate);
            // 如果当前用户未登录，则直接当成游客处理所有用户
            if (!isLoggingUser(_this.userInfoDomain.userInfoAggregate.userInfo.userId)) {
                _this.checkAdd2CollaborateList(collaborate, true);
                hasUpdated = true;
                return;
            }
            if (_this.collabAllInfoList.find(function (item) { return item.userId === userId; })) {
                return;
            }
            // 如果是游客，不用请求头像直接加入队列
            if (!isLoggingUser(notice.userId)) {
                _this.checkAdd2CollaborateList(collaborate, true);
                hasUpdated = true;
                return;
            }
            // 如果用户之前进过房间，现在又进来了, 从缓存取一下, 直接返回回去
            if (_this.collaboratesCacheMap.has(userId)) {
                var cacheCollaborate = _this.collaboratesCacheMap.get(userId);
                var vipInfo = safeParseObject(notice.vipInfo, {});
                var joinVipInfoCollaborate = __assign(__assign({}, cacheCollaborate), { vipInfo: vipInfo });
                _this.checkAdd2CollaborateList(joinVipInfoCollaborate, false);
                hasUpdated = true;
                return;
            }
            // 加入待拉取资料队列
            _this.needRequestUserIds.add(userId);
        });
        if (hasUpdated) {
            this.onCollabAllInfoListUpdatedEmitter.fire({ collabAllInfoList: this.collabAllInfoList });
        }
        // 如果当前用户未登录，则直接当成游客处理所有用户
        if (!isLoggingUser(this.userInfoDomain.userInfoAggregate.userInfo.userId)) {
            return;
        }
        this.throttleGetUserMarkname();
    };
    /**
     * 限频拉取用户信息
     *
     * 1、首次不做延迟，直接拉取
     * 2、之后延迟 this.throttleInterval 或者 THROTTLE_INTERVAL
     */
    CollaborateAggregate.prototype.throttleGetUserMarkname = function () {
        var _this = this;
        if (this.throttleTimer) {
            return;
        }
        // 首次不做延迟，直接拉取
        this.innerGetUserMarkName();
        // 节流&批量请求, 之后延迟 this.throttleInterval 或者 THROTTLE_INTERVAL
        var interval = this.throttleInterval || THROTTLE_INTERVAL;
        this.throttleTimer = setInterval(function () {
            _this.innerGetUserMarkName();
        }, interval);
    };
    CollaborateAggregate.prototype.processUserLeave = function (notices) {
        var _this = this;
        notices === null || notices === void 0 ? void 0 : notices.notices.forEach(function (notice) {
            var userId = getUidFormUserId(notice.userId);
            // 先把当前流水弹出
            _this.popFormCollabRecordList(userId);
            // 如果同一个 userId 还有其它端的 uuid 在房间内，则不把此用户移除协作者列表
            if (_this.collabRecordList.filter(function (item) { return item.userId === userId; }).length > 0) {
                return;
            }
            _this.collabAllInfoList = _this.collabAllInfoList.filter(function (item) { return item.userId !== userId; });
            // 如果是非登录用户，不用请求头像直接加入队列
            if (!isLoggingUser(notice.userId)) {
                return;
            }
            _this.needRequestUserIds.delete(userId);
        });
        this.onCollabAllInfoListUpdatedEmitter.fire({ collabAllInfoList: this.collabAllInfoList });
    };
    // 拉取协作者资料
    CollaborateAggregate.prototype.innerGetUserMarkName = function () {
        return __awaiter(this, void 0, void 0, function () {
            var uids, res, rsp, error_1;
            var _this = this;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (this.needRequestUserIds.size === 0) {
                            return [2 /*return*/];
                        }
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        uids = Array.from(this.needRequestUserIds).join(',');
                        return [4 /*yield*/, this.collaborateRepository.getUserMarkName(uids)];
                    case 2:
                        res = _b.sent();
                        if (((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.retcode) === 0 /* RETCODE.SUCCESS */) {
                            rsp = res.data.result.rsp;
                            if (!rsp) {
                                return [2 /*return*/];
                            }
                            rsp.forEach(function (item) {
                                if (_this.needRequestUserIds.has(item.reqUid)) {
                                    _this.needRequestUserIds.delete(item.reqUid);
                                    _this.checkAdd2CollaborateList(item, false);
                                }
                                _this.collaboratesCacheMap.set(item.reqUid, item);
                            });
                            // 批量拉完一波，统一发事件
                            this.onCollabAllInfoListUpdatedEmitter.fire({ collabAllInfoList: this.collabAllInfoList });
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _b.sent();
                        CollaborateAggregate_1.logger.error('handleUserComeIn request error');
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    CollaborateAggregate.prototype.popFormCollabRecordList = function (userId) {
        for (var i = 0; i < this.collabRecordList.length; i++) {
            if (this.collabRecordList[i].userId === userId) {
                this.collabRecordList.splice(i, 1);
                return;
            }
        }
    };
    /**
     * 缓存 getusermarkname CGI 的回包数据
     * 自动处理批量请求多个 userIds 的 Array 返回值的缓存逻辑
     */
    CollaborateAggregate.prototype.setUserMarkItemToCache = function (resData) {
        var _this = this;
        var _a, _b, _c, _d;
        if (0 /* RETCODE.SUCCESS */ !== ((_a = resData === null || resData === void 0 ? void 0 : resData.data) === null || _a === void 0 ? void 0 : _a.retcode)) {
            return;
        }
        var items = (_d = (_c = (_b = resData === null || resData === void 0 ? void 0 : resData.data) === null || _b === void 0 ? void 0 : _b.result) === null || _c === void 0 ? void 0 : _c.rsp) !== null && _d !== void 0 ? _d : [];
        items.forEach(function (item) {
            if (!(item === null || item === void 0 ? void 0 : item.reqUid)) {
                return;
            }
            _this.collaboratesCacheMap.set(item.reqUid, item);
        });
    };
    /**
     * 通过单个 userId 从缓存中获取结果
     * 如果未缓存则返回 userId ，缓存则返回缓存结果
     * @param userId userId
     * @returns 未缓存的 userId 列表以及缓存结果
     */
    CollaborateAggregate.prototype.getSingleUserMarkItemFromCache = function (userId) {
        var collaborate = this.collaboratesCacheMap.get(userId);
        if (collaborate) {
            return {
                unCachedUserIds: [],
                cachedResults: [collaborate],
            };
        }
        return {
            unCachedUserIds: [userId],
            cachedResults: [],
        };
    };
    /**
     * 从本地缓存数据中获取协作者信息
     * @param userIds userId 列表或单个 userId
     * @returns 尚未缓存的 userId 以及 获取到的缓存结果
     */
    CollaborateAggregate.prototype.getUserMarkItemFromCache = function (userIds) {
        var _this = this;
        var unCachedIdsArray = [];
        var cachedResultsArray = [];
        var userIdsArray = isArray(userIds) ? userIds : [userIds];
        userIdsArray.forEach(function (userId) {
            var _a = _this.getSingleUserMarkItemFromCache(userId), cachedResults = _a.cachedResults, unCachedUserIds = _a.unCachedUserIds;
            unCachedIdsArray = unCachedIdsArray.concat(unCachedUserIds);
            cachedResultsArray = cachedResultsArray.concat(cachedResults);
        });
        return {
            unCachedUserIds: unCachedIdsArray,
            cachedResults: cachedResultsArray,
        };
    };
    var CollaborateAggregate_1;
    __decorate([
        dataFieldNamingHandler(),
        exceptionHandler(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, Object, Object]),
        __metadata("design:returntype", Promise)
    ], CollaborateAggregate.prototype, "getUserMarkName", null);
    __decorate([
        dataFieldNamingHandler(),
        exceptionHandler(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number, String]),
        __metadata("design:returntype", Promise)
    ], CollaborateAggregate.prototype, "setPersonalizeAnonymous", null);
    __decorate([
        dataFieldNamingHandler(),
        exceptionHandler(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", Promise)
    ], CollaborateAggregate.prototype, "getPersonalizeAnonymous", null);
    CollaborateAggregate = CollaborateAggregate_1 = __decorate([
        __param(1, IDocMetaDomainService),
        __param(2, IUserInfoDomainService),
        __metadata("design:paramtypes", [String, Object, Object])
    ], CollaborateAggregate);
    return CollaborateAggregate;
}(Aggregate));
export { CollaborateAggregate };
